// /sign-in — ported from design-handoff/src/pages/signin.jsx.
//
// 2-column hero+form layout on a dark gradient canvas with red glows
// and a subtle grid background. Form card has glassy elevation, OAuth
// buttons stacked, email/password underneath, "Create workspace" +
// "Forgot password" footer links, plus the trademark line at bottom.
//
// Auth wiring preserved from previous implementation:
//   useSignIn() -> POST /v1/auth/login -> session cookie -> /app.
//   401 from backend renders an inline error; other errors fall back
//   to a generic message.

import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useSignIn } from "@/lib/auth";
import { ApiError } from "@/lib/api";
import { Icons, NDMark } from "@/components/icons";
import { AuthLangToggle } from "@/routes/sign-up";

export const Route = createFileRoute("/sign-in")({
  component: SignInPage,
});

function SignInPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const nav = useNavigate();
  const signIn = useSignIn();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);

  // Surface OAuth callback failures (?oauth_error=…) as an inline message.
  // Codes match those returned by identity-service's CallbackGoogleOAuth.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const oauthErr = params.get("oauth_error");
    if (!oauthErr) return;
    const map: Record<string, { tr: string; en: string }> = {
      state_mismatch: {
        tr: "Google girişi süresi doldu, lütfen tekrar dene.",
        en: "Google sign-in expired, please try again.",
      },
      access_denied: {
        tr: "Google girişi iptal edildi.",
        en: "Google sign-in was cancelled.",
      },
      token_exchange: {
        tr: "Google ile bağlantı kurulamadı, lütfen tekrar dene.",
        en: "Couldn't reach Google, please try again.",
      },
      userinfo: {
        tr: "Google profili alınamadı.",
        en: "Couldn't fetch your Google profile.",
      },
    };
    const msg = map[oauthErr];
    setErr(msg ? (tr ? msg.tr : msg.en)
      : (tr ? "Google girişi başarısız oldu." : "Google sign-in failed."));
    // Strip the error from the URL so it doesn't stick around on refresh.
    const url = new URL(window.location.href);
    url.searchParams.delete("oauth_error");
    window.history.replaceState({}, "", url.toString());
  }, [tr]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      await signIn.mutateAsync({ email: email.trim().toLowerCase(), password });
      await nav({ to: "/app" });
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        setErr(e.status === 401
          ? (tr ? "E-posta veya parola yanlış." : "Email or password is wrong.")
          : e.message);
      } else {
        setErr(tr ? "Bir hata oluştu, lütfen tekrar dene." : "Something went wrong, please try again.");
      }
    }
  }

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "var(--bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* Background — subtle grid + two red glows */}
      <div className="bg-grid" style={{ position: "absolute", inset: 0, opacity: 0.5 }} />
      <AuthLangToggle />
      <div
        style={{
          position: "absolute",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(224,45,28,0.2), transparent 60%)",
          right: -200,
          top: -200,
          filter: "blur(60px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(224,45,28,0.1), transparent 60%)",
          left: "20%",
          bottom: -100,
          filter: "blur(60px)",
        }}
      />

      <div
        className="auth-grid"
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 60,
          alignItems: "center",
          maxWidth: 1100,
          width: "90vw",
        }}
      >
        {/* Left — hero */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
            <NDMark size={42} />
            <div>
              <div style={{ fontFamily: "var(--display)", fontWeight: 800, fontSize: 18, letterSpacing: "-0.02em" }}>
                <span style={{ color: "var(--red)" }}>N</span>EXUS{" "}
                <span style={{ color: "var(--red)" }}>D</span>İJİTAL
              </div>
              <div className="mono" style={{ fontSize: 10, color: "var(--mu)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                İstanbul · Private beta · 2026
              </div>
            </div>
          </div>

          <h1 style={{ fontFamily: "var(--display)", fontWeight: 800, fontSize: 56, letterSpacing: "-0.03em", lineHeight: 1.02, marginBottom: 20 }}>
            {tr ? (
              <>İçeriğin beyni.<br /><span style={{ color: "var(--red)" }}>Yayıncı değil.</span></>
            ) : (
              <>The content brain.<br /><span style={{ color: "var(--red)" }}>Not the publisher.</span></>
            )}
          </h1>
          <p style={{ fontSize: 16, color: "var(--tx2)", maxWidth: 480, lineHeight: 1.6, marginBottom: 32 }}>
            {tr
              ? "Tüm markaların için tek workspace. Her birinin sesini öğrenen AI. 5 platforma yerel gönderiler. Günlük üretim yapan ajanslar, freelancerlar ve operatörler için."
              : "One workspace for every brand you run. AI that learns each one's voice. Native posts across 5 platforms. Built for agencies, freelancers, and operators producing daily content at scale."}
          </p>

          {/* Marketing facts row — replaces the fake '1,400+/8/98%' KPI
              stats which were static literals presented as live metrics.
              These are product facts, not analytics, so they don't lie. */}
          <div style={{ display: "flex", gap: 24, paddingTop: 24, borderTop: "1px solid var(--b1)" }}>
            {([
              ["5", tr ? "Platform" : "Platforms"],
              ["5dk", tr ? "Onboarding" : "Onboarding"],
              ["KVKK", tr ? "uyumlu" : "compliant"],
            ] as const).map(([v, l]) => (
              <div key={l}>
                <div className="t-num" style={{ fontSize: 22, color: "var(--tx)" }}>{v}</div>
                <div className="mono" style={{ fontSize: 10, color: "var(--mu)", letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 4 }}>
                  {l}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — form card */}
        <div className="card" style={{ padding: 32, background: "var(--surface)", boxShadow: "0 30px 80px rgba(0,0,0,0.5)" }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>— {tr ? "Giriş" : "Sign in"}</div>
          <h2 style={{ fontSize: 24, marginBottom: 22 }}>{tr ? "Tekrar hoş geldin." : "Welcome back."}</h2>

          {/* OAuth — Google flow live as of identity-service v0.3.14.
              Apple login can be added later when an Apple Developer
              account is in place. */}
          <a
            href={`${__API_BASE_URL__}/v1/auth/oauth/google/start?return_to=/app`}
            className="btn btn-secondary"
            style={{ width: "100%", justifyContent: "center", padding: "11px 16px", textDecoration: "none" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77a6.6 6.6 0 0 1-9.81-3.47H2.18v2.85A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M6.42 14.1A6.6 6.6 0 0 1 6.07 12c0-.72.12-1.43.35-2.1V7.05H2.18a11 11 0 0 0 0 9.9l4.24-2.85z" />
              <path fill="#EA4335" d="M12 5.4c1.62 0 3.07.56 4.21 1.65L19.36 4A11 11 0 0 0 12 1a11 11 0 0 0-9.82 6.05L6.42 9.9A6.6 6.6 0 0 1 12 5.4z" />
            </svg>
            {tr ? "Google ile devam et" : "Continue with Google"}
          </a>

          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0", color: "var(--mu)", fontSize: 11, fontFamily: "var(--mono)" }}>
            <div style={{ flex: 1, height: 1, background: "var(--b1)" }} />
            {tr ? "VEYA E-POSTA" : "OR EMAIL"}
            <div style={{ flex: 1, height: 1, background: "var(--b1)" }} />
          </div>

          <form onSubmit={onSubmit}>
            <input
              className="input"
              placeholder={tr ? "ornek@firma.com" : "ali@mesh.studio"}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ marginBottom: 10 }}
            />
            <input
              className="input"
              placeholder={tr ? "Parola" : "Password"}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ marginBottom: 18 }}
            />

            {err ? (
              <div role="alert" style={{ color: "var(--error)", fontSize: 12, marginBottom: 14, marginTop: -8 }}>
                {err}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={signIn.isPending || !email || !password}
              className="btn btn-primary"
              style={{ width: "100%", justifyContent: "center", padding: "11px 16px", opacity: signIn.isPending ? 0.7 : 1 }}
            >
              {signIn.isPending ? (tr ? "Giriş yapılıyor…" : "Signing in…") : (tr ? "Giriş yap" : "Sign in")}
              <Icons.ArrowRight size={14} />
            </button>
          </form>

          <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--mu)" }}>
            <Link to="/sign-up" className="btn btn-ghost btn-sm">
              {tr ? "Hesap oluştur" : "Create workspace"}
            </Link>
            <Link to="/forgot-password" className="btn btn-ghost btn-sm">
              {tr ? "Parolanı mı unuttun?" : "Forgot password"}
            </Link>
          </div>
        </div>
      </div>

      {/* Trademark footer — handoff verbatim */}
      <div
        className="mono"
        style={{
          position: "absolute",
          bottom: 20,
          left: 0,
          right: 0,
          textAlign: "center",
          fontSize: 10,
          color: "var(--mu)",
          letterSpacing: "0.15em",
          textTransform: "uppercase",
        }}
      >
        Mesh Yazılım Teknoloji Ltd. Şti. ·{" "}
        <span style={{ color: "var(--red)" }}>Trademark Reg. 2024/161525</span> · İstanbul
      </div>
    </div>
  );
}
